import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, isApiError } from "../../api/client.js";
import { demoAssets } from "../shared/constants.js";
import { useMobileViewport } from "../shared/useMobileViewport.js";
import { CodeField, PasswordField, type AuthConfig } from "./CompanyAuthFields.js";
import { codeStateKey, readCodeState, writeCodeState, type CodePurpose } from "./companyCodeState.js";

type Method = "code" | "password" | "email" | "reset";

/** Only this documented business rejection can select the existing-user path. */
function isRegisteredPhone(error: unknown) {
  return isApiError(error) && error.status === 400
    && error.code === "company_send_code_failed"
    && error.message === "该手机号已被注册";
}

export function CompanyUnifiedAuth({ config, onAuthed, sessionNotice, mobile }: {
  config: AuthConfig;
  onAuthed: () => void;
  sessionNotice?: string;
  mobile: boolean;
}) {
  const viewportRef = useMobileViewport(mobile);
  const [method, setMethod] = useState<Method>("code");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [sent, setSent] = useState<{ phone: string; purpose: CodePurpose } | null>(null);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const [invalidField, setInvalidField] = useState<"phone" | "code" | "agreement" | null>(null);
  const [notice, setNotice] = useState("");
  const requestId = useRef(0);
  const sendBusy = useRef(false);
  const submitBusy = useRef(false);
  const [stateKey, setStateKey] = useState("");
  const phoneRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const agreementRef = useRef<HTMLInputElement>(null);
  const validPhone = /^1\d{10}$/.test(phone.trim());

  useEffect(() => () => { requestId.current++; }, []);
  useEffect(() => {
    let active = true;
    setStateKey("");
    setCooldown(0);
    if (validPhone) void codeStateKey(phone.trim()).then((key) => {
      if (!active) return;
      setStateKey(key);
      const saved = readCodeState(key);
      setSent(saved.purpose ? { phone: phone.trim(), purpose: saved.purpose } : null);
    });
    return () => { active = false; };
  }, [phone, validPhone]);
  useEffect(() => {
    const update = () => setCooldown(Math.max(0, Math.ceil((readCodeState(stateKey).retryAt - Date.now()) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [stateKey]);

  function invalidateCode() {
    requestId.current++;
    sendBusy.current = false;
    setSending(false);
    setSent(null);
    setCode("");
    setError("");
    setInvalidField(null);
    setNotice("");
  }

  function changeMethod(next: Method) {
    if (next === method) return;
    requestId.current++;
    setError("");
    setInvalidField(null);
    setNotice("");
    if ((next === "reset") !== (method === "reset")) setCode("");
    setPassword("");
    setMethod(next);
  }

  async function sendCode() {
    if (sendBusy.current || submitBusy.current || !validPhone || !stateKey || readCodeState(stateKey).retryAt > Date.now()) return;
    if (method === "code" && !agreed) {
      setError("请先阅读并同意用户协议与隐私政策。");
      setInvalidField("agreement");
      agreementRef.current?.focus();
      return;
    }
    const currentId = ++requestId.current;
    const targetPhone = phone.trim();
    const targetKey = stateKey;
    const isCurrent = () => currentId === requestId.current;
    let purpose: CodePurpose = method === "reset" ? "resetPassword" : "register";
    sendBusy.current = true;
    setSending(true);
    setSent(null);
    setCode("");
    setError("");
    setNotice("");
    // A new/ambiguous send can replace the upstream code. Never reuse the previous purpose.
    writeCodeState(targetKey, { retryAt: 0 });
    const requestCode = () => api<{ cooldownTime: number }>("/api/auth/send-code", {
      method: "POST",
      body: JSON.stringify({ phoneNumber: targetPhone, purpose, ...(purpose === "register" ? { agreeTerms: true } : {}) })
    });
    try {
      let result;
      try {
        result = await requestCode();
      } catch (caught) {
        if (!isCurrent()) return;
        if (purpose !== "register" || !isRegisteredPhone(caught)) throw caught;
        purpose = "login";
        result = await requestCode();
      }
      // Remember a real send cooldown even when the user has since edited the phone.
      const seconds = Math.max(1, result.cooldownTime || 60);
      writeCodeState(targetKey, { retryAt: Date.now() + seconds * 1000, purpose, expiresAt: Date.now() + 15 * 60_000 });
      if (!isCurrent()) return;
      setCooldown(seconds);
      setSent({ phone: targetPhone, purpose });
      setNotice("验证码已发送，请注意查收。");
      codeRef.current?.focus();
    } catch (caught) {
      const retry = isApiError(caught) ? Number((caught.data as { cooldownTime?: number } | undefined)?.cooldownTime) : 0;
      if (Number.isFinite(retry) && retry > 0 && retry <= 86400) {
        writeCodeState(targetKey, { retryAt: Date.now() + Math.ceil(retry) * 1000 });
        if (isCurrent()) setCooldown(Math.ceil(retry));
      }
      if (isCurrent()) setError(caught instanceof Error ? caught.message : "验证码发送失败，请稍后重试。");
    } finally {
      if (isCurrent()) {
        sendBusy.current = false;
        setSending(false);
      }
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitBusy.current || sendBusy.current) return;
    setError("");
    setInvalidField(null);
    setNotice("");
    if (method !== "email" && !validPhone) {
      setError("请输入 11 位有效手机号。");
      setInvalidField("phone");
      phoneRef.current?.focus();
      return;
    }
    if (method === "code" && !agreed) {
      setError("请先阅读并同意用户协议与隐私政策。");
      setInvalidField("agreement");
      agreementRef.current?.focus();
      return;
    }
    if (method === "code" || method === "reset") {
      if (!sent || sent.phone !== phone.trim() || (method === "reset") !== (sent.purpose === "resetPassword")) {
        setError("请先获取验证码。");
        return;
      }
      if (!code.trim() || (method === "reset" && code.trim().length < 4)) {
        setError(method === "reset" ? "请输入至少 4 位验证码。" : "请输入验证码。");
        setInvalidField("code");
        codeRef.current?.focus();
        return;
      }
    }
    if (method === "reset" && password.length < 6) {
      setError("新密码至少需要 6 位。");
      return;
    }
    submitBusy.current = true;
    setSubmitting(true);
    try {
      if (method === "reset") {
        await api("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ phoneNumber: phone.trim(), code: code.trim(), newPassword: password }) });
        writeCodeState(stateKey, { retryAt: readCodeState(stateKey).retryAt });
        setSent(null);
        changeMethod("password");
        setNotice("密码重置成功，请使用新密码登录。");
      } else {
        const register = method === "code" && sent?.purpose === "register";
        const body = register
          ? { phone: phone.trim(), verificationCode: code.trim(), agreeTerms: true }
          : method === "code"
            ? { method: "phone_code", phone: phone.trim(), verificationCode: code.trim() }
            : method === "password"
              ? { method: "phone_password", phone: phone.trim(), password }
              : { method: "email_password", email: email.trim(), password };
        await api(register ? "/api/auth/register" : "/api/auth/login", { method: "POST", body: JSON.stringify(body) });
        writeCodeState(stateKey, { retryAt: readCodeState(stateKey).retryAt });
        onAuthed();
      }
    } catch (caught) {
      // Never try the other endpoint with a code issued for this purpose.
      setError(caught instanceof Error ? caught.message : "操作失败，请稍后重试。");
    } finally {
      submitBusy.current = false;
      setSubmitting(false);
    }
  }

  return (
    <main ref={viewportRef} className={`auth-shell company-auth-shell${mobile ? " mobile-auth-shell" : ""}`}>
      <form className={`auth-panel company-auth-panel${mobile ? " mobile-auth-panel" : ""}`} onSubmit={submit}>
        <header className={mobile ? "mobile-auth-heading" : "company-auth-heading"}>
          <img className="auth-logo" src={demoAssets.mark} alt="" />
          {mobile && <strong>TruthSeeker</strong>}
          <h1>{mobile ? method === "reset" ? "重置登录密码" : "欢迎使用 TruthSeeker" : "TruthSeeker"}</h1>
          <p>{method === "reset" ? mobile ? "验证手机号后设置新密码" : "重置登录密码" : "登录或注册，开始科研问答"}</p>
        </header>
        {sessionNotice && <p className="auth-session-notice" role="status">{sessionNotice}</p>}
        <fieldset disabled={submitting || sending}>
          {method !== "reset" && <div className="company-login-methods" role="tablist" aria-label="登录方式">
            <button type="button" role="tab" aria-selected={method === "code"} className={method === "code" ? "active" : ""} onClick={() => changeMethod("code")}>验证码登录</button>
            <button type="button" role="tab" aria-selected={method === "password" || method === "email"} className={method !== "code" ? "active" : ""} onClick={() => changeMethod("password")}>密码登录</button>
          </div>}
          {method === "email"
            ? <label>邮箱地址<input value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" inputMode="email" placeholder="请输入邮箱地址" /></label>
            : <label>手机号<input ref={phoneRef} value={phone} aria-invalid={invalidField === "phone" || undefined} aria-describedby={invalidField === "phone" ? "company-auth-error" : undefined} onChange={(event) => { invalidateCode(); setStateKey(""); setPhone(event.target.value); }} autoComplete="tel" inputMode="tel" placeholder="请输入手机号" /></label>}
          {(method === "code" || method === "reset") && <CodeField value={code} onChange={(value) => { setCode(value); if (invalidField === "code") { setError(""); setInvalidField(null); } }} onSend={sendCode} cooldown={cooldown} busy={sending} sendDisabled={!validPhone || !stateKey || (method === "code" && !agreed)} inputRef={codeRef} error={invalidField === "code" ? error : undefined} errorId="company-auth-code-error" />}
          {method !== "code" && <PasswordField label={method === "reset" ? "新密码" : "密码"} value={password} onChange={setPassword} autoComplete={method === "reset" ? "new-password" : "current-password"} />}
          {method === "code" && <label className="auth-agreement">
            <input ref={agreementRef} type="checkbox" checked={agreed} aria-invalid={invalidField === "agreement" || undefined} aria-describedby={invalidField === "agreement" ? "company-auth-error" : undefined} onChange={(event) => { setAgreed(event.target.checked); setError(""); setInvalidField(null); }} />
            <span>我已阅读并同意 <a href={config.legal?.termsUrl || "/legal/terms/v1.0"} target="_blank" rel="noreferrer">《用户协议》</a> 与 <a href={config.legal?.privacyUrl || "/legal/privacy/v1.0"} target="_blank" rel="noreferrer">《隐私政策》</a></span>
          </label>}
          {error && invalidField !== "code" && <p id="company-auth-error" className="auth-error" role="alert">{error}</p>}
          {notice && <p className="auth-notice" role="status">{notice}</p>}
          <button className="primary-action" type="submit" disabled={sending || submitting}>{submitting ? "处理中…" : method === "code" ? "登录或注册" : method === "reset" ? "重置密码" : "登录"}</button>
          <footer className="company-auth-footer">
            {method === "reset" ? <button type="button" onClick={() => changeMethod("code")}>返回登录</button> : <>
              <button type="button" onClick={() => changeMethod("reset")}>忘记密码？</button>
              {method !== "code" && <button type="button" onClick={() => changeMethod(method === "email" ? "password" : "email")}>{method === "email" ? "手机密码登录" : "邮箱密码登录"}</button>}
            </>}
          </footer>
        </fieldset>
      </form>
    </main>
  );
}
